import React, { FC, useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import cx from "classnames";
import Button from "../../components/Button";
import { invalidAgentIDMessage, isInvalidAgentLogin, validAgentId } from "../../views/AgentLogin/agentLoginHelper";
import { IAxiosResponse } from "../../axiosTypes";
import Agent from "../../models/Agent";
import { NCD_AGENT_SUPPORT_PHONE, NCD_AGENT_SUPPORT_TRACK_INVALID_PHONE } from "../../utils";
import ErrorModal from "../Modals/ErrorModal";
import useApplicationStore from "../../context/application";
import { AxiosResponse } from "axios";

type AgentLoginFormProps = {
  agent: Agent;
  setAgentState: React.Dispatch<React.SetStateAction<Agent>>;
}

interface AgentLoginFormLocationState {
  invalidAgentID: boolean
}

const AgentLoginForm: FC<AgentLoginFormProps> = ({ agent, setAgentState }: AgentLoginFormProps) => {
  const [agentID, setAgentID] = useState('');
  const navigate = useNavigate();

  const [invalidAgentID, setInvalidAgentID] = useState(false);
  const [invalidAnimation, setInvalidAnimation] = useState(false);
  const [pending, setPending] = useState(false);
  const { state } = useLocation();
  const appId = useApplicationStore((state) => state.appId );
  const agentLoginFormLocationState: AgentLoginFormLocationState = state as AgentLoginFormLocationState; // warning: could be null
  const agentSupportPhoneRef = useRef(NCD_AGENT_SUPPORT_PHONE);

  // When we have redirected back to the agentLogin form, we include state along with navigation
  useEffect(() => {
    if(agentLoginFormLocationState?.invalidAgentID) {
      // this should trigger the invalidAgentID useEffect + animation:
      agentSupportPhoneRef.current = NCD_AGENT_SUPPORT_TRACK_INVALID_PHONE;
      setInvalidAgentID(true)
    }
  }, [agentLoginFormLocationState])

  useEffect(() => {
    if (invalidAgentID === true) {
      setInvalidAgentID(false);
      setInvalidAnimation(true);
      setTimeout(() => setInvalidAnimation(false), 5000);
    }
  }, [invalidAgentID]);

  // todo move into validation func
  useEffect(() => {
    document
      .querySelector("input")
      ?.addEventListener("keypress", function (evt) {
        if (evt.key !== "Enter") {
          if (evt.which < 48 || evt.which > 57) {
            evt.preventDefault();
          }
        }
      });
  }, []);

  const handleAgentLoginSubmit = useCallback((event: React.FormEvent ) => {
    event.preventDefault();
    setPending(true);
    if (!validAgentId(agentID)) {
      setInvalidAgentID(true)
      setPending(false);
      return;
    }
    agent.id = Number(agentID);
    agent.loginAgent(agent.id, setAgentState, appId)
      .then((data: AxiosResponse | IAxiosResponse) => {
        const invalid = isInvalidAgentLogin(data as IAxiosResponse);
        setInvalidAgentID(invalid); // type assertion, because TS wants IAxiosResponse | AxiosResponse. But, IAxiosResponse should incl AxiosResponse

        setPending(false);
        if (invalid) return;

        navigate(`/${agentID}`);
      });
  }, [agent, agentID, appId, navigate, setAgentState, setInvalidAgentID, setPending])

  return (
    <form
      onSubmit={handleAgentLoginSubmit}
      className="relative z-50 flex h-[60%] flex-col items-center justify-center self-center justify-self-center px-2 font-inter sm:h-[70%]"
    >
      <div>
        <div className="rounded-3xl border-4 border-black bg-white p-4 xs:p-8 sm:px-[75px] sm:py-[51px]">
          <div
            className={cx(
              "text-lg leading-[24.2px]",
              "sm:text-xl",
              "text-left font-bold text-black"
            )}
          >
            Please enter your Agent ID
          </div>
          <div className="mt-[15px] flex flex-col items-start justify-start text-navyBlue">
            <div className="flex flex-col items-center justify-center gap-3 md:flex-row">
              <div className="flex flex-col">
                <input
                  className={cx(
                    "h-[40px] w-full md:max-w-[215px]",
                    "appearance-none rounded-lg",
                    "border border-solid border-black bg-backgroundSolidGrey",
                    "pl-3 text-xl leading-[24.2px] focus:outline-none"
                  )}
                  type="number"
                  maxLength={6}
                  value={agentID}
                  onChange={(event) => {
                    setAgentID(
                      event.target.value.slice(0, 6).replace(/\D/g, "")
                    );
                    setInvalidAgentID(false);
                  }}
                />
                {invalidAnimation && (
                  <p className="mt-2 text-red md:hidden">
                    {invalidAgentIDMessage}
                  </p>
                )}
              </div>
              <div
                className={cx(
                  "h-[40px] w-full text-xl leading-[24.2px]",
                  "md:h-[40px] md:w-[121.38px] md:max-w-[145px]"
                )}
              >
                <Button text={"Submit"} disabled={pending} loadingIcon={pending} submit mainPath />
              </div>
            </div>
            <p
              className="mt-[15px] self-start text-start text-base font-medium leading-[19.36px] text-black"
            >
              Can't remember your Agent ID?
            </p>
            <a
              href={`tel:${agentSupportPhoneRef.current}`}
              className="mt-[5px] cursor-pointer self-start text-lg font-bold leading-[19.36px] text-celadonBlue"
            >
              Call {agentSupportPhoneRef.current}
            </a>

            <br />

            <ErrorModal message={invalidAgentIDMessage} visible={invalidAnimation} />
          </div>
        </div>
      </div>
    </form>
)}

export default AgentLoginForm;
